import React, { useEffect, useState, useRef } from "react";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { Toast } from "primereact/toast";
import { InputSwitch } from "primereact/inputswitch";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { TabView, TabPanel } from "primereact/tabview";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import {
  runMorningSync,
  getSyncStatus,
  syncCotizacionesPendientes,
} from "../../offline/syncService";
import { getRegId } from "../../offline/regId";
import { useOfflineMode } from "../../offline/offlineMode";
import { listarCotizacionesLocalesOffline } from "../../offline/offlineCotizacionService";

import * as offlineCatalogo from "../../offline/offlineCatalogoService";

export default function OfflineSyncPage() {
  const toast = useRef(null);

  const [syncing, setSyncing] = useState(false);
  const [syncingCotizaciones, setSyncingCotizaciones] = useState(false);
  const [progress, setProgress] = useState(null);
  const [status, setStatus] = useState([]);
  const [offlineMode, setOfflineMode] = useOfflineMode();
  const [cotizacionesLocales, setCotizacionesLocales] = useState([]);
  const [cotizacionesFiltradas, setCotizacionesFiltradas] = useState([]);
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroDesde, setFiltroDesde] = useState(null);
  const [filtroHasta, setFiltroHasta] = useState(null);

  const loadStatus = async () => {
    const rows = await getSyncStatus();
    setStatus(rows);
  };

  const cargarCotizacionesLocales = async () => {
    const data = await listarCotizacionesLocalesOffline();
    setCotizacionesLocales(data);
    setCotizacionesFiltradas(data);
  };

  const loadAll = async () => {
    await Promise.all([loadStatus(), cargarCotizacionesLocales()]);
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    window.offlineCatalog = offlineCatalogo;
  }, []);

  const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();

    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");

    return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
  };

  const handleProgress = async (nextProgress) => {
    setProgress(nextProgress);

    if (nextProgress?.tipo === "COTIZACION") {
      await cargarCotizacionesLocales();
    }
  };

  const handleSync = async () => {
    if (syncing) return;

    setSyncing(true);
    setProgress(null);

    try {
      const total = await runMorningSync(handleProgress);

      toast.current.show({
        severity: "success",
        summary: "Sincronización completa",
        detail: `Servicios sincronizados: ${total}`,
        life: 4000,
      });

      await loadAll();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error de sincronización",
        detail: error.message,
        life: 6000,
      });

      await loadAll();
    } finally {
      setSyncing(false);
    }
  };

  const handleBuscarCotizaciones = () => {
    const cliente = String(filtroCliente || "")
      .trim()
      .toLowerCase();

    const desde = filtroDesde
      ? new Date(
          filtroDesde.getFullYear(),
          filtroDesde.getMonth(),
          filtroDesde.getDate(),
          0,
          0,
          0,
        )
      : null;

    const hasta = filtroHasta
      ? new Date(
          filtroHasta.getFullYear(),
          filtroHasta.getMonth(),
          filtroHasta.getDate(),
          23,
          59,
          59,
        )
      : null;

    const filtradas = cotizacionesLocales.filter((item) => {
      const codigoCliente = String(item.codigoCliente || "").toLowerCase();
      const razonSocial = String(
        item.cliente?.razonSocial ||
          item.cliente?.razon_social ||
          item.cliente?.razon ||
          "",
      ).toLowerCase();

      const matchCliente =
        !cliente ||
        codigoCliente.includes(cliente) ||
        razonSocial.includes(cliente);

      const fecha = item.fechaCreacion ? new Date(item.fechaCreacion) : null;

      const matchDesde = !desde || (fecha && fecha >= desde);
      const matchHasta = !hasta || (fecha && fecha <= hasta);

      return matchCliente && matchDesde && matchHasta;
    });

    setCotizacionesFiltradas(filtradas);
  };

  const handleLimpiarFiltrosCotizaciones = () => {
    setFiltroCliente("");
    setFiltroDesde(null);
    setFiltroHasta(null);
    setCotizacionesFiltradas(cotizacionesLocales);
  };

  const handleSyncCotizaciones = async () => {
    if (syncingCotizaciones) return;

    setSyncingCotizaciones(true);
    setProgress(null);

    try {
      const result = await syncCotizacionesPendientes(handleProgress);

      toast.current.show({
        severity: result.errores > 0 ? "warn" : "success",
        summary: "Sincronización de cotizaciones",
        detail: `Enviadas: ${result.ok}. Errores: ${result.errores}. Total: ${result.total}`,
        life: 5000,
      });

      await cargarCotizacionesLocales();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error sincronizando cotizaciones",
        detail: error.message,
        life: 6000,
      });

      await cargarCotizacionesLocales();
    } finally {
      setSyncingCotizaciones(false);
    }
  };

  const progressValue = progress?.total
    ? Math.round((progress.actual / progress.total) * 100)
    : progress?.tipo === "COTIZACION"
    ? 100
    : 0;

  const globalStatus = status.find((item) => item.recurso === "GLOBAL");

  const totalServicios = status.filter(
    (item) => item.recurso !== "GLOBAL",
  ).length;

  const serviciosOk = status.filter(
    (item) => item.recurso !== "GLOBAL" && item.estado === "OK",
  ).length;

  const serviciosError = status.filter(
    (item) => item.recurso !== "GLOBAL" && item.estado === "ERROR",
  ).length;

  const cotizacionFueRecibidaPorBackend = (item) =>
    Boolean(
      item.idServidor ||
        item.folioServidor ||
        item.estadoCuarentena === "EN_CUARENTENA" ||
        item.respuestaSync?.id,
    );

  const cotizacionesEnCuarentena = cotizacionesLocales.filter((item) =>
    cotizacionFueRecibidaPorBackend(item),
  ).length;

  const cotizacionesPendientes = cotizacionesLocales.filter((item) => {
    if (cotizacionFueRecibidaPorBackend(item)) return false;

    return ["PENDIENTE", "ERROR", "ERROR_SYNC", "ENVIANDO"].includes(
      item.estadoSync || "PENDIENTE",
    );
  }).length;

  const estadoSyncTemplate = (rowData) => {
    const estado = rowData.estadoSync || "PENDIENTE";

    const severityByEstado = {
      PENDIENTE: "warning",
      ENVIANDO: "info",
      SINCRONIZADA: "success",
      ERROR: "danger",
      ERROR_SYNC: "danger",
      DESCARTADA: "secondary",
    };

    return (
      <Tag value={estado} severity={severityByEstado[estado] || "warning"} />
    );
  };

  const estadoCatalogoTemplate = (rowData) => {
    const severityByEstado = {
      OK: "success",
      SINCRONIZANDO: "info",
      ERROR: "danger",
      OK_CON_ERRORES: "warning",
      OMITIDO: "secondary",
      SIN_TABLA_LOCAL: "warning",
    };

    return (
      <Tag
        value={rowData.estado || "-"}
        severity={severityByEstado[rowData.estado] || "secondary"}
      />
    );
  };

  const cuarentenaTemplate = (rowData) => {
    if (!rowData.estadoCuarentena) return "-";

    return <Tag value={rowData.estadoCuarentena} severity="warning" />;
  };

  const clienteTemplate = (rowData) => {
    const codigo = rowData.codigoCliente || "-";
    const razonSocial =
      rowData.cliente?.razonSocial ||
      rowData.cliente?.razon_social ||
      rowData.cliente?.razon ||
      "";

    return razonSocial ? `${codigo} - ${razonSocial}` : codigo;
  };

  const servidorTemplate = (rowData) => {
    if (rowData.folioServidor) return rowData.folioServidor;
    if (rowData.idServidor) return rowData.idServidor;
    return "-";
  };

  const responseTemplate = (rowData) => {
    if (!rowData.respuestaSync) return "-";

    const texto =
      typeof rowData.respuestaSync === "string"
        ? rowData.respuestaSync
        : JSON.stringify(rowData.respuestaSync);

    return (
      <span title={texto}>
        {texto.length > 60 ? `${texto.slice(0, 60)}...` : texto}
      </span>
    );
  };

  const errorTemplate = (rowData) => {
    if (!rowData.ultimoErrorSync) return "-";

    return (
      <span className="text-red-500" title={rowData.ultimoErrorSync}>
        {String(rowData.ultimoErrorSync).slice(0, 80)}
      </span>
    );
  };

  return (
    <div className="p-4">
      <Toast ref={toast} />

      <div className="surface-card p-4 shadow-2 border-round-xl">
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
          <div>
            <h2 className="m-0">Modo offline</h2>
            <p className="text-600 mb-0">
              Sincroniza catálogos y envía cotizaciones creadas localmente.
            </p>
          </div>

          <div className="flex align-items-center gap-2">
            <span className="font-medium">
              {offlineMode ? "Offline activado" : "Online"}
            </span>
            <InputSwitch
              checked={offlineMode}
              onChange={(e) => setOfflineMode(e.value)}
            />
          </div>
        </div>

        <div className="grid mb-4">
          <div className="col-12 md:col-3">
            <div className="surface-100 border-round p-3 h-full">
              <div className="text-500 text-sm">Estado general</div>
              <div className="text-xl font-bold mt-1">
                {globalStatus?.estado || "SIN SINCRONIZAR"}
              </div>
              <div className="text-600 text-sm mt-2">
                Última sync: {formatDateTime(globalStatus?.ultimaSync)}
              </div>
            </div>
          </div>

          <div className="col-12 md:col-3">
            <div className="surface-100 border-round p-3 h-full">
              <div className="text-500 text-sm">Catálogos OK</div>
              <div className="text-xl font-bold mt-1">
                {serviciosOk}/{totalServicios}
              </div>
              <div className="text-600 text-sm mt-2">
                Errores: {serviciosError}
              </div>
            </div>
          </div>

          <div className="col-12 md:col-3">
            <div className="surface-100 border-round p-3 h-full">
              <div className="text-500 text-sm">Cotizaciones locales</div>
              <div className="text-xl font-bold mt-1">
                {cotizacionesLocales.length}
              </div>
              <div className="text-600 text-sm mt-2">
                Pendientes/Error: {cotizacionesPendientes}
              </div>
            </div>
          </div>

          <div className="col-12 md:col-3">
            <div className="surface-100 border-round p-3 h-full">
              <div className="text-500 text-sm">Cotizaciones en cuarentena</div>
              <div className="text-xl font-bold mt-1">
                {cotizacionesEnCuarentena}
              </div>
              <div className="text-600 text-sm mt-2">RegId: {getRegId()}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            label={
              syncing ? "Sincronizando catálogos..." : "Sincronizar catálogos"
            }
            icon={syncing ? "pi pi-spin pi-spinner" : "pi pi-database"}
            disabled={syncing || syncingCotizaciones}
            onClick={handleSync}
          />

          <Button
            label={
              syncingCotizaciones
                ? "Enviando cotizaciones..."
                : "Sincronizar cotizaciones"
            }
            icon={syncingCotizaciones ? "pi pi-spin pi-spinner" : "pi pi-send"}
            className="p-button-outlined"
            disabled={syncing || syncingCotizaciones}
            onClick={handleSyncCotizaciones}
          />

          <Button
            label="Refrescar"
            icon="pi pi-refresh"
            className="p-button-outlined"
            disabled={syncing || syncingCotizaciones}
            onClick={loadAll}
          />
        </div>

        {progress && (
          <div className="mb-4">
            <div className="flex justify-content-between mb-2">
              <span>
                {progress.tipo === "COTIZACION"
                  ? `Cotización ${progress.nombre}`
                  : progress.nombre}
              </span>

              <span className="font-bold">
                {progress.tipo === "COTIZACION"
                  ? progress.estado
                  : `${progress.actual}/${progress.total}`}
              </span>
            </div>

            <ProgressBar
              value={progressValue}
              style={{ height: "2rem" }}
              displayValueTemplate={() => `${progressValue}%`}
            />
          </div>
        )}

        <TabView>
          <TabPanel
            header="Catálogos sincronizados"
            leftIcon="pi pi-database mr-2"
          >
            <DataTable
              value={status.filter((item) => item.recurso !== "GLOBAL")}
              size="small"
              emptyMessage="Todavía no hay sincronizaciones."
              className="p-datatable-sm border-1 surface-border border-round"
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 50]}
              responsiveLayout="scroll"
            >
              <Column field="recurso" header="Recurso" sortable />
              <Column header="Estado" body={estadoCatalogoTemplate} sortable />
              <Column
                header="Última sync"
                body={(rowData) => formatDateTime(rowData.ultimaSync)}
                sortable
              />
              <Column field="cantidadRegistros" header="Registros" sortable />
              <Column header="Error" body={errorTemplate} />
            </DataTable>
          </TabPanel>

          <TabPanel header="Cotizaciones locales" leftIcon="pi pi-file mr-2">
            <div className="flex justify-content-between align-items-center mb-3">
              <div>
                <h3 className="m-0">Cotizaciones locales</h3>
                <p className="text-600 mb-0">
                  Cotizaciones creadas sin conexión y su estado de envío.
                </p>
              </div>

              <Button
                label="Refrescar"
                icon="pi pi-refresh"
                className="p-button-outlined p-button-sm"
                onClick={async () => {
                  await cargarCotizacionesLocales();
                }}
                disabled={syncing || syncingCotizaciones}
              />
            </div>

            <div className="grid mb-3">
              <div className="col-12 md:col-4">
                <label className="text-500 font-medium text-xs block mb-1">
                  Cliente
                </label>
                <InputText
                  value={filtroCliente}
                  onChange={(e) => setFiltroCliente(e.target.value)}
                  placeholder="Código o razón social"
                  className="w-full p-inputtext-sm"
                />
              </div>

              <div className="col-12 md:col-3">
                <label className="text-500 font-medium text-xs block mb-1">
                  Desde
                </label>
                <Calendar
                  value={filtroDesde}
                  onChange={(e) => setFiltroDesde(e.value)}
                  dateFormat="dd-mm-yy"
                  placeholder="Desde"
                  className="w-full p-inputtext-sm"
                  showIcon
                />
              </div>

              <div className="col-12 md:col-3">
                <label className="text-500 font-medium text-xs block mb-1">
                  Hasta
                </label>
                <Calendar
                  value={filtroHasta}
                  onChange={(e) => setFiltroHasta(e.value)}
                  dateFormat="dd-mm-yy"
                  placeholder="Hasta"
                  className="w-full p-inputtext-sm"
                  showIcon
                />
              </div>

              <div className="col-12 md:col-2 flex align-items-end gap-2">
                <Button
                  label="Buscar"
                  icon="pi pi-search"
                  className="p-button-sm"
                  onClick={handleBuscarCotizaciones}
                />

                <Button
                  icon="pi pi-times"
                  className="p-button-outlined p-button-sm"
                  onClick={handleLimpiarFiltrosCotizaciones}
                  tooltip="Limpiar filtros"
                />
              </div>
            </div>

            <DataTable
              value={cotizacionesFiltradas}
              size="small"
              emptyMessage="No hay cotizaciones locales"
              className="p-datatable-sm border-1 surface-border border-round"
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 50]}
              responsiveLayout="scroll"
            >
              <Column header="Estado" body={estadoSyncTemplate} />
              <Column field="folioLocal" header="Folio local" sortable />
              <Column header="Cliente" body={clienteTemplate} sortable />
              <Column field="tipoDocumento" header="Tipo" sortable />
              <Column
                header="Fecha creación"
                body={(rowData) => formatDateTime(rowData.fechaCreacion)}
                sortable
              />
              <Column
                header="Último intento"
                body={(rowData) => formatDateTime(rowData.fechaUltimoIntento)}
                sortable
              />
              <Column header="Servidor" body={servidorTemplate} />
              <Column
                header="Sincronizada"
                body={(rowData) => formatDateTime(rowData.fechaSincronizacion)}
                sortable
              />
              <Column header="Cuarentena" body={cuarentenaTemplate} />
              <Column header="Respuesta" body={responseTemplate} />
              <Column header="Error" body={errorTemplate} />
            </DataTable>
          </TabPanel>
        </TabView>
      </div>
    </div>
  );
}
